import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { File, GroupedFiles, Folder } from '../../types/file.types';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CIPXScrollableContentComponent } from '@sbd-doug-frontend/shared/lib/ui';
import { environment } from '@sbd-doug-frontend/app/environments/environment';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';

@Component({
  selector: 'sbd-doug-frontend-file-cabinet',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatExpansionModule,
    MatListModule,
    MatButtonModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatTreeModule,
    CIPXScrollableContentComponent,
  ],
  templateUrl: './file-cabinet.component.html',
  styleUrls: ['./file-cabinet.component.scss'],
})
export class FileCabinetComponent implements OnInit {
  @Input() set files(value: any | undefined) {
    // Handle the wrapper if it exists
    if (value && value.file_cabinet) {
      this._files = value.file_cabinet;
    } else {
      this._files = value;
    }
    this.updateDataSource();
    this.resetToRoot();
  }
  get files(): GroupedFiles | undefined {
    return this._files;
  }
  private _files: GroupedFiles | undefined;

  path: { name: string; data: Folder | GroupedFiles }[] = [];
  currentFolder: { name: string; data: Folder | GroupedFiles } | undefined;

  viewType: 'tile' | 'tree' = 'tile';

  treeControl = new NestedTreeControl<any>((node) => this.getChildren(node));
  dataSource = new MatTreeNestedDataSource<any>();

  ngOnInit(): void {
    this.resetToRoot();
  }

  updateDataSource(): void {
    if (this._files) {
      this.dataSource.data = this._files.folders || [];
    } else {
      this.dataSource.data = [];
    }
  }

  hasChild = (_: number, node: any) => {
    if (!this.isFolder(node)) {
      return false;
    }
    const folders = node.folders || node.subfolders || [];
    const files = (node.files || []).filter((f: File) => !this.isBlankFile(f));
    return folders.length > 0 || files.length > 0;
  };

  getChildren(node: any): any[] {
    const folders = node.folders || node.subfolders || [];
    const files = (node.files || []).filter((f: File) => !this.isBlankFile(f));
    return [...folders, ...files];
  }

  isFolder(node: any): boolean {
    return node.folders !== undefined || node.subfolders !== undefined || node.label !== undefined;
  }

  isBlankFile(file: File): boolean {
    return (!file.file_name && !file.file_friendly_name && !file.file_size) || !file.file_id;
  }

  resetToRoot(): void {
    this.path = [];
    if (this._files) {
      this.currentFolder = { name: 'Root', data: this._files };
    } else {
      this.currentFolder = { name: 'Root', data: { folders: [] } };
    }
  }

  isGroupedFiles(data: Folder | GroupedFiles): data is GroupedFiles {
    return (data as GroupedFiles).folders !== undefined;
  }

  getFolders(data: Folder | GroupedFiles): Folder[] {
    if (this.isGroupedFiles(data)) {
      return data.folders || [];
    }
    return data.subfolders || [];
  }

  getFiles(data: Folder | GroupedFiles): File[] {
    if (this.isGroupedFiles(data)) {
      return [];
    }
    return (data.files || []).filter((f) => !this.isBlankFile(f));
  }

  navigateToFolder(folder: Folder): void {
    this.path.push({ name: folder.label, data: folder });
    this.currentFolder = { name: folder.label, data: folder };
  }

  navigateBack(index: number): void {
    if (index === -1) {
      this.resetToRoot();
    } else {
      this.path = this.path.slice(0, index + 1);
      this.currentFolder = this.path[this.path.length - 1];
    }
  }

  viewFile(file: File): void {
    window.open(`${environment.sbdBaseUrl}/admin/files/view/?file_id=${file.file_id}`, '_blank');
  }

  downloadFile(file: File): void {
    window.open(`${environment.sbdBaseUrl}/admin/files/download/?file_id=${file.file_id}`, '_blank');
  }


  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
